import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ActivityServices } from '../../follow-ups/services/activities.services';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { CKEditorLoaderService } from 'src/app/shared/ckeditor-loader.service';
import { FlexiMessageService, BusinessEditorDto } from '../../../services/flexi-message.service';
import { BaseServices } from '../../kuba.services';

interface RecipientOption {
  id: number;
  name: string;
  email?: string;
}

/** One day's worth of messages in the thread, oldest day first. */
interface ChatDay {
  /** yyyy-mm-dd, used as the *ngFor trackBy key. */
  key: string;
  /** Midnight of the day, for the date pipe. Null when the API sent no date. */
  date: Date | null;
  /** 0 = today, 1 = yesterday, -1 = older (render the date instead). */
  relative: number;
  items: ChatMessage[];
}

interface ChatMessage {
  id?: number;
  text: string;
  createdAt: Date;
  username: string;
  messageContent?: string;
  fromUserId?: number;
  fromUserName?: string;
  toUserId?: number;
  toUserName?: string;
  isRead?: boolean;
}

@Component({
  selector: 'advisor-detail',
  templateUrl: 'my-hse-advisor.component.html',
  styleUrls: ['my-hse-advisor.component.scss'],
  standalone: false
})
export class MyHSEAdvisor implements OnInit {
  userData: any;
  businessId: number;
  currentUserId: number;
  currentUsername = '';

  // Chat state
  newMessageText = '';
  messages: ChatMessage[] = [];

  // Recipients - will be populated from API
  recipientOptions: RecipientOption[] = [];
  selectedRecipients: RecipientOption[] = [];

  // Role-based flags
  isAdmin = false;
  isEditor = false;
  showRecipientField = false;
  isPortalRemoteReadOnly = false;

  // Message detail dialog
  showMessageDialog = false;
  selectedMessage: ChatMessage | null = null;

  // Loading states
  isLoadingMessages = false;
  isLoadingRecipients = false;
  isSubmitting = false;

  /** "HSE advisor, Kuba IK" — the application the advisor works for. */
  readonly applicationName = BaseServices.ApplicationName;

  /** The message box "Send message to advisor" takes the user to. */
  @ViewChild('messageInput') private messageInput?: ElementRef<HTMLTextAreaElement>;

  constructor(
    private activityService: ActivityServices,
    private route: ActivatedRoute,
    public router: Router,
    private translate: TranslateService,
    private ckEditorLoader: CKEditorLoaderService,
    private flexiMessageService: FlexiMessageService
  ) { }

  ngOnInit() {
    $('body').removeClass('opened');

    this.ckEditorLoader.isScriptLoaded().subscribe((loaded: boolean) => {
      if (loaded) {
        // CKEditor script has been loaded
      }
    });

    // Get data from route resolver
    // Image stays as the API sent it — null included. The template renders an
    // initials avatar when there is no photo, which reads better than the
    // grey silhouette the old default-avatar.png gave every advisor.
    this.userData = this.route.snapshot.data['list'];

    // Use BaseServices for all user context — handles R-mode correctly
    this.businessId = BaseServices.BusinessId;
    this.currentUserId = BaseServices.UserId;
    this.currentUsername = BaseServices.Name || BaseServices.userName || 'You';

    /**
     * Role detection (advisor side vs. editor/customer side):
     *  - AbsoluteroleId is the real role regardless of R-mode: Admin = '1',
     *    Portal = '2', Editor = '3' (mirrors flexi.component.ts /
     *    flexi-compliance.service.ts, the source of truth for Flexi).
     *  - Admin is the OHS-advisor side. AbsoluteroleId stays '1' even in
     *    R-mode, so checking it === '1' covers admin-direct AND admin-in-R-mode
     *    while NOT promoting a Portal user (who is editor-equivalent in Flexi)
     *    to the advisor side — not even when that Portal user is in R-mode.
     *    The previous bare `IsRmode` clause did exactly that and is removed.
     *  - A non-admin HSE advisor reaches advisor functions via server gating,
     *    not this UI flag.
     */
    const absoluteRoleId = BaseServices.AbsoluteroleId;

    this.isAdmin = String(absoluteRoleId) === '1'; // Admin only
    this.isEditor = !this.isAdmin;
    this.isPortalRemoteReadOnly =
      BaseServices.IsRmode && String(absoluteRoleId) === '2';

    // Recipient selection only for admins (advisors sending to business editors)
    this.showRecipientField = this.isAdmin && !this.isPortalRemoteReadOnly;

    // Load business editors for recipient selection (only for admin OHS-advisors)
    if (this.isAdmin) {
      this.loadBusinessEditors();
    }

    // Load messages
    this.loadMessages();
  }

  /** "Send message to advisor": bring the message box into view and focus it. */
  focusComposer(): void {
    const input = this.messageInput?.nativeElement;
    if (!input) return;
    input.scrollIntoView({ behavior: 'smooth', block: 'center' });
    input.focus({ preventScroll: true });
  }

  /**
   * Load business editors for recipient dropdown (only visible to admins)
   */
  loadBusinessEditors(): void {
    if (!this.businessId) return;

    this.isLoadingRecipients = true;
    this.flexiMessageService.getBusinessRecipients(this.businessId).subscribe(
      (recipients: any[]) => {
        // Map PascalCase API response to lowercase properties expected by RecipientOption and PrimeNG
        this.recipientOptions = (recipients || []).map(r => ({
          id: r.id ?? r.Id,
          name: r.name ?? r.Name,
          email: r.email ?? r.Email
        }));
        this.isLoadingRecipients = false;
      },error => {
        console.error('Error loading recipients:', error);
        this.isLoadingRecipients = false;
      }
    );
  }

  /**
   * Parse a date value from the API safely.
   * ASP.NET Core may return dates as PascalCase (CreatedOn) or camelCase (createdOn),
   * and some dates may be null or an invalid string.
   */
  private safeDate(raw: any): Date | null {
    if (!raw) return null;
    const d = new Date(raw);
    return isNaN(d.getTime()) ? null : d;
  }

  /**
   * Load messages for current business and user
   */
  loadMessages(): void {
    if (!this.businessId) {
      console.warn('loadMessages - businessId is not set');
      return;
    }

    this.isLoadingMessages = true;

    this.flexiMessageService.getBusinessMessages(this.businessId).subscribe(
      (messages: any[]) => {
        if (!messages || messages.length === 0) {
          this.messages = [];
        } else {
          this.messages = messages.map(m => ({
            id: m.id ?? m.Id,
            text: m.messageContent ?? m.MessageContent ?? m.text ?? '',
            messageContent: m.messageContent ?? m.MessageContent ?? m.text ?? '',
            // Handle both camelCase (ASP.NET default) and PascalCase responses
            createdAt: this.safeDate(m.createdOn ?? m.CreatedOn),
            username: m.fromUserName ?? m.FromUserName ?? m.username ?? '',
            fromUserId: m.fromUserId ?? m.FromUserId ?? 0,
            fromUserName: m.fromUserName ?? m.FromUserName ?? '',
            toUserId: m.toUserId ?? m.ToUserId ?? 0,
            toUserName: m.toUserName ?? m.ToUserName ?? '',
            isRead: m.isRead ?? m.IsRead ?? false
          }));
        }

        this.isLoadingMessages = false;
      },error => {
        console.error('loadMessages - Error loading messages:', error);
        this.isLoadingMessages = false;
      }
    );
  }

  sendMessage(): void {
    if (this.isPortalRemoteReadOnly) return;
    const text = (this.newMessageText || '').trim();

    if (!text || this.isSubmitting) return;

    if (!this.businessId || this.businessId <= 0) {
      console.error('sendMessage - Invalid businessId:', this.businessId);
      return;
    }

    if (this.isAdmin && !this.selectedRecipients?.length) {
      return;
    }

    this.isSubmitting = true;

    if (this.isAdmin) {
      // Send to each selected editor
      const requests = this.selectedRecipients.map(recipient => {
        const messageToCreate = {
          businessId: this.businessId,
          toUserId: recipient.id,
          messageContent: text,
          repliedToMessageId: null,
          cultureInfo: BaseServices.userCultureInfo()
        };
        return this.flexiMessageService.createMessage(messageToCreate);
      });

      // We use a simple counter to refresh once at the end
      let completedCount = 0;
      requests.forEach(req => {
        req.subscribe({
          next: () => {
            completedCount++;
            if (completedCount === requests.length) {
              this.loadMessages();
              this.selectedRecipients = [];
              this.newMessageText = '';
              this.isSubmitting = false;
            }
          },
          error:err => {
            console.error('Error sending message:', err);
            completedCount++;
            if (completedCount === requests.length) {
              this.isSubmitting = false;
            }
          }
        });
      });
    } else {
      // Editor → send to HSE advisor
      const messageToCreate = {
        businessId: this.businessId,
        toUserId: this.userData?.Id || 0,
        messageContent: text,
        repliedToMessageId: null,
        cultureInfo: BaseServices.userCultureInfo()
      };

      this.flexiMessageService.createMessage(messageToCreate).subscribe({
        next: (response: any) => {
          if (response) {
            this.loadMessages();
            this.newMessageText = '';
          }
          this.isSubmitting = false;
        },
        error:error => {
          console.error('sendMessage - Error sending message to advisor:', error);
          this.isSubmitting = false;
        }
      });
    }
  }

  /**
   * Deletes a message identified by the object, not by its position.
   *
   * The thread renders day groups built from a sorted copy, so a row index no
   * longer lines up with `messages`. The index is resolved here instead.
   */
  deleteMessage(m: ChatMessage): void {
    if (!this.isAdmin || this.isPortalRemoteReadOnly) return;
    if (this.messages.indexOf(m) < 0) return;

    // Not yet persisted — drop it locally and stop.
    if (!m.id) {
      this.messages = this.messages.filter(item => item !== m);
      return;
    }

    this.flexiMessageService.deleteMessage(m.id).subscribe(
      () => {
        // Removed by identity rather than by a position captured before the
        // round-trip: the list may have been reloaded while it was in flight.
        this.messages = this.messages.filter(item => item !== m);
      },error => {
        console.error('Error deleting message:', error);
      }
    );
  }

  /**
   * Open a message in the detail dialog and mark it as read
   */
  openMessage(m: ChatMessage): void {
    this.selectedMessage = m;
    this.showMessageDialog = true;

    if (!this.isPortalRemoteReadOnly && m.id && !m.isRead && m.toUserId === this.currentUserId) {
      this.flexiMessageService.markMessageAsRead(m.id).subscribe(
        () => { m.isRead = true; },error => { console.error('Error marking as read:', error); }
      );
    }
  }

  closeMessageDialog(): void {
    this.showMessageDialog = false;
    this.selectedMessage = null;
  }

  /**
   * Pre-fill reply textarea with a reference to the selected message sender,
   * then close the dialog so the user can type their reply.
   */
  replyToMessage(): void {
    if (this.isPortalRemoteReadOnly) return;
    if (!this.selectedMessage) return;
    const senderName = this.selectedMessage.fromUserName || this.selectedMessage.username || '';
    this.newMessageText = `Re: ${senderName} – `;

    // For editor replies, the advisor is always the recipient (userData).
    // For admin, pre-select the original sender as recipient.
    if (this.isAdmin) {
      const senderOption = this.recipientOptions.find(
        r => r.id === this.selectedMessage!.fromUserId
      );
      if (senderOption) {
        this.selectedRecipients = [senderOption];
      }
    }

    this.closeMessageDialog();
  }

  /**
   * "Storgata 12 · 0155 · Oslo" — whichever of the three the advisor record
   * actually carries.
   *
   * The admin user form writes the place name to either Address.Location
   * ("Sted") or Address.City ("Poststed" — its PostalAddress field), so City
   * is the fallback when Location is blank.
   */
  get advisorLocation(): string {
    if (!this.userData) return '';
    return [this.userData.Address, this.userData.ZipCode,
            this.userData.Location || this.userData.City]
      .map(value => (value == null ? '' : String(value).trim()))
      .filter(value => value.length > 0)
      .join(' · ');
  }

  /** trackBy for the day groups — the key is already the day. */
  trackByDay(_index: number, day: ChatDay): string {
    return day.key;
  }

  /**
   * Up to two initials for the avatar, e.g. "Kari Nordmann" -> "KN".
   * Empty string when there is no usable name, which hides the avatar text
   * and leaves the tinted circle.
   */
  initials(name: string | null | undefined): string {
    const parts = (name || '').trim().split(/\s+/).filter(p => p.length > 0);
    if (parts.length === 0) return '';
    const first = parts[0].charAt(0);
    const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : '';
    return (first + last).toUpperCase();
  }

  /** True for a message this user sent, which sits on the right of the thread. */
  isOwnMessage(m: ChatMessage): boolean {
    return m.fromUserId === this.currentUserId;
  }

  /** True for a message addressed to this user that has not been opened yet. */
  isUnread(m: ChatMessage): boolean {
    return !m.isRead && m.toUserId === this.currentUserId;
  }

  /** How many unread messages the tab badge reports. */
  get unreadCount(): number {
    return this.messages.filter(m => this.isUnread(m)).length;
  }

  /**
   * The thread, oldest first, split into day groups.
   *
   * Sorting happens here rather than in loadMessages so `messages` keeps the
   * order the API sent and the index-based delete stays honest. Messages whose
   * date failed to parse are kept — they group under a null date and render
   * without a separator label rather than disappearing.
   */
  get messageDays(): ChatDay[] {
    const sorted = [...this.messages].sort((a, b) => {
      const at = a.createdAt ? a.createdAt.getTime() : 0;
      const bt = b.createdAt ? b.createdAt.getTime() : 0;
      return at - bt;
    });

    const days: ChatDay[] = [];
    let current: ChatDay | null = null;

    for (const m of sorted) {
      const key = this.dayKey(m.createdAt);
      if (!current || current.key !== key) {
        current = {
          key,
          date: m.createdAt ? this.startOfDay(m.createdAt) : null,
          relative: this.daysAgo(m.createdAt),
          items: []
        };
        days.push(current);
      }
      current.items.push(m);
    }

    return days;
  }

  private startOfDay(d: Date): Date {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }

  private dayKey(d: Date | null): string {
    if (!d) return 'unknown';
    return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
  }

  /** 0 = today, 1 = yesterday, -1 = anything else (or no date). */
  private daysAgo(d: Date | null): number {
    if (!d) return -1;
    const today = this.startOfDay(new Date()).getTime();
    const then = this.startOfDay(d).getTime();
    const diff = Math.round((today - then) / 86400000);
    return diff === 0 || diff === 1 ? diff : -1;
  }

  /**
   * Returns the name to show in the "From" column of the message table.
   * - If we sent the message → show who we sent it TO (so row reads person we are talking to)
   * - If we received it → show who sent it (sender's name)
   */
  getMessageDisplayName(m: ChatMessage): string {
    // Always show the name of who sent the message (from/sender perspective)
    const name = m.fromUserName || m.username;

    if (!name || name === 'Unknown' || name === '(Unknown)') {
      return this.isAdmin ? 'Business Editor' : 'HSE Advisor';
    }
    return name;
  }
}
